import React, { useMemo } from 'react'

import { Button, HStack, Icon, T, VStack } from 'govwell-ui'

import AIAssistantUpsellModal from 'src/components/AIAssistant/AIAssistantUpsellModal'
import { useAICommunityAssistantUsageQuery } from 'src/fetch/aiChat'
import useDisclosure from 'src/hooks/use-disclosure'
import { useMyOrg, useMyOrgId } from 'src/layouts/AppStateContextLayout/utils'
import {
  getReverseTrialDaysLeftInTimeZone,
  getReverseTrialSeverity,
  ReverseTrialSeverity,
} from 'src/utils/aicaReverseTrial'
import { DateFormats, formatDateInTimeZone } from 'src/utils/date'

type SeverityStyles = {
  container: string
  icon: string
  pill: string
  body: string
}

const SEVERITY_STYLES: Record<ReverseTrialSeverity, SeverityStyles> = {
  default: {
    container:
      'rounded-xl border border-violet-200 bg-gradient-to-r from-violet-50 to-indigo-50 px-5 py-4',
    icon: 'text-violet-600',
    pill: 'rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-semibold text-emerald-700',
    body: 'text-neutral-600',
  },
  warning: {
    container:
      'rounded-xl border border-yellow-300 bg-gradient-to-r from-yellow-50 to-amber-50 px-5 py-4',
    icon: 'text-yellow-600',
    pill: 'rounded-full bg-yellow-200 px-2 py-0.5 text-xs font-semibold text-yellow-800',
    body: 'text-yellow-900',
  },
  critical: {
    container: 'rounded-xl border border-red-300 bg-gradient-to-r from-red-50 to-rose-50 px-5 py-4',
    icon: 'text-red-600',
    pill: 'rounded-full bg-red-200 px-2 py-0.5 text-xs font-semibold text-red-800',
    body: 'text-red-900',
  },
  expired: {
    container: 'rounded-xl border border-red-300 bg-gradient-to-r from-red-50 to-rose-50 px-5 py-4',
    icon: 'text-red-600',
    pill: 'rounded-full bg-red-200 px-2 py-0.5 text-xs font-semibold text-red-800',
    body: 'text-red-900',
  },
}

const AICAReverseTrialStatusCard = () => {
  const org = useMyOrg()
  const organizationId = useMyOrgId()

  const isReverseTrial = org?.aiResidentAssistantUsageTier === 'AicaReverseTrial'

  const { aiCommunityAssistantUsage } = useAICommunityAssistantUsageQuery({
    organizationId,
    date: { type: 'ThisMonth' },
  })

  const upsellModalState = useDisclosure()

  const trialEndDateText = useMemo(() => {
    if (!org?.trialEndDate) {
      return undefined
    }
    return formatDateInTimeZone(org.trialEndDate, org.timeZone, DateFormats.MonthNameDateNoTime)
  }, [org?.trialEndDate, org?.timeZone])

  const daysLeft = useMemo(
    () => getReverseTrialDaysLeftInTimeZone(org?.trialEndDate, org?.timeZone),
    [org?.trialEndDate, org?.timeZone]
  )

  if (!isReverseTrial) {
    return null
  }

  const severity = getReverseTrialSeverity(daysLeft)
  const isExpired = severity === 'expired'
  const styles = SEVERITY_STYLES[severity]
  const usedResponses = aiCommunityAssistantUsage?.usedResponses ?? 0
  const tiers = aiCommunityAssistantUsage?.tiers ?? []

  return (
    <>
      <div className="px-4 pt-4 md:px-6">
        <div className={styles.container}>
          <HStack justify="space-between" align="center" gap={4} wrap="wrap">
            <VStack align="start" gap={2} className="min-w-0 flex-1">
              <HStack align="center" gap={3} wrap="wrap">
                <HStack align="center" gap={2}>
                  <Icon.FaSparkles className={styles.icon} />
                  <T.BodyLarge className="font-semibold whitespace-nowrap text-neutral-950">
                    {isExpired
                      ? 'Your free trial of AI Community Assistant has expired'
                      : 'Premium Trial'}
                  </T.BodyLarge>
                </HStack>
                <T.BodySmall as="span" className={styles.pill}>
                  {isExpired ? 'Expired' : 'Active'}
                </T.BodySmall>
                {trialEndDateText && (
                  <T.Body className="text-neutral-700">
                    {isExpired ? (
                      <>
                        Trial expired on <strong>{trialEndDateText}</strong>
                      </>
                    ) : (
                      <>
                        Trial ends <strong>{trialEndDateText}</strong>
                      </>
                    )}
                  </T.Body>
                )}
              </HStack>
              <T.Body className={styles.body}>
                {isExpired ? (
                  'Please upgrade ASAP to avoid service disruptions.'
                ) : (
                  <>
                    <strong>{usedResponses}</strong> questions this month · You&rsquo;re enjoying
                    Premium features for free during your trial period.
                  </>
                )}
              </T.Body>
            </VStack>
            <Button onClick={upsellModalState.open}>View plans</Button>
          </HStack>
        </div>
      </div>
      {upsellModalState.isOpen && (
        <AIAssistantUpsellModal onClose={upsellModalState.close} tiers={tiers} />
      )}
    </>
  )
}

export default React.memo(AICAReverseTrialStatusCard)
